import React, { memo, useCallback, useMemo } from 'react'
import { useFieldArray } from 'react-hook-form'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { useSavedLaneFormContext } from '../../context/SavedLaneFormContext'
import { getErrorMessage } from '../../schema'
import { TRecipientSchema } from '../../schema/RecipientSchema'
import AutocompleteRecipientUser from './AutocompleteRecipientUser'
import { IAutocompleteRecipientUserProps } from './AutocompleteRecipientUser/AutocompleteRecipientUser'
import RecipientRow from './RecipientRow'
import { useRecipientsFormSectionStyles } from './useRecipientsFormSectionStyles'

export interface IRecipientsFormSectionProps {
  carrierApplicationId: string
}

const RecipientsFormSection = memo<IRecipientsFormSectionProps>(({ carrierApplicationId }) => {
  const classes = useRecipientsFormSectionStyles()
  /**
   * Form Context
   */
  const { control, errors } = useSavedLaneFormContext()
  const recipientErrors = getErrorMessage(errors, 'recipients')

  /**
   * Recipients Field Array
   */
  const { append, fields: recipients, remove } = useFieldArray<TRecipientSchema>({
    control,
    name: 'recipients',
  })
  const userIds = useMemo(
    () =>
      recipients.map(({ user }) => {
        const { id: userId } = user || {}
        return userId
      }),
    [recipients]
  )

  const selectUserHandler: IAutocompleteRecipientUserProps['onChange'] = useCallback(
    user => {
      if (user) {
        const { email, id: userId, name, text: userLabel } = user || {}
        if (userId && !userIds.includes(userId)) {
          append({
            user: { ...user, text: userLabel || '', name: name || '', email: email || '' },
            receiveNotifications: true,
          })
        }
      }
    },
    [append, userIds]
  )

  return (
    <div>
      <Box mb={1.5}>
        <Typography variant='subtitle1'>Recipients</Typography>
      </Box>
      <AutocompleteRecipientUser
        carrierApplicationId={carrierApplicationId}
        onChange={selectUserHandler}
        inputValidationMessage={recipientErrors}
      />
      {recipients.length > 0 && (
        <div className={classes.recipients}>
          {recipients.map((recipient, index) => {
            const { user } = recipient || {}
            const { id: userId } = user || {}
            return (
              <RecipientRow
                key={userId}
                recipientsIndex={index}
                recipient={recipient}
                onRemove={remove}
              />
            )
          })}
        </div>
      )}
    </div>
  )
})

export default RecipientsFormSection
